import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Lock, UserPlus, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../lib/AuthContext';
import { api, errMsg, isUpgradeErr, fileUrl } from '../lib/api';
import { manilaDate, PLAN_LABELS, peso } from '../lib/format';
import { useUpgrade, planLabel } from '../components/UpgradeModal';

export default function SettingsPage() {
  const { me, config, refreshMe } = useAuth();
  const { openUpgrade } = useUpgrade();
  const shop = me?.shop;
  const features = shop?.features || {};
  const logoRef = useRef(null);
  const [form, setForm] = useState({ name: '', address: '', tin: '', mobile: '', slug: '' });
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'advisor' });

  useEffect(() => {
    if (shop) setForm({ name: shop.name, address: shop.address || '', tin: shop.tin || '', mobile: shop.mobile || '', slug: shop.slug || '' });
  }, [shop]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/shop/users');
      setUsers(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { loadUsers(); }, []);

  const saveShop = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: form.name, address: form.address, tin: form.tin, mobile: form.mobile };
      if (!shop.slug_edited && form.slug !== shop.slug) payload.slug = form.slug;
      await api.patch('/shop', payload);
      toast.success('Shop settings saved');
      refreshMe();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/shop/logo', fd);
      toast.success('Logo updated');
      refreshMe();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const addUser = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/shop/users', newUser);
      toast.success(data.message);
      setAddOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'advisor' });
      loadUsers();
    } catch (e) {
      if (isUpgradeErr(e)) openUpgrade(planLabel(e.response.data.detail.required_plan));
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const toggleUser = async (u) => {
    try {
      const { data } = await api.patch(`/shop/users/${u.id}`, { active: !u.active });
      toast.success(data.message);
      loadUsers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!shop) return null;
  const planPrice = config?.plans?.[shop.plan]?.monthly_price_centavos;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <h1 className="text-xl font-semibold font-display">Shop settings</h1>

      {/* Plan & subscription */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold font-display">Plan & subscription</h2>
            <span data-testid="settings-plan-badge" className="rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] px-3 py-1 text-xs font-semibold">
              {PLAN_LABELS[shop.plan]}{planPrice ? ` · ${peso(planPrice)}/mo` : ''}
            </span>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Status: <span className="font-medium text-[hsl(var(--foreground))] capitalize">{shop.subscription_status.replace('_', ' ')}</span>
            {shop.subscription_status === 'trialing' && ` · trial ends ${manilaDate(shop.trial_ends_at)}`}
            {shop.paid_until && ` · paid until ${manilaDate(shop.paid_until)}`}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            To upgrade or renew, contact us: <span className="font-medium text-[hsl(var(--foreground))]">{config?.platform_mobile} / {config?.platform_email}</span>
          </p>
        </CardContent>
      </Card>

      {/* Shop info */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5">
          <h2 className="text-base font-semibold font-display mb-3">Shop information</h2>
          <form onSubmit={saveShop} className="space-y-3">
            <Input data-testid="settings-shop-name-input" required placeholder="Shop name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="settings-address-input" placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11 rounded-xl" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="settings-tin-input" placeholder="TIN (optional)" value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} className="h-11 rounded-xl" />
              <Input data-testid="settings-mobile-input" placeholder="Shop mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <div className="space-y-1">
              <Input data-testid="settings-slug-input" placeholder="Link name (slug)" value={form.slug} disabled={shop.slug_edited} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="h-11 rounded-xl" />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{shop.slug_edited ? 'The link name has already been changed once and is now locked.' : 'The link name can be changed once.'}</p>
            </div>
            <div className="flex items-center gap-3">
              {shop.logo_url && <img src={fileUrl(shop.logo_url)} alt="logo" className="h-12 w-12 rounded-xl object-cover border border-black/5" />}
              <Button data-testid="settings-logo-upload-button" type="button" variant="outline" className="h-10 rounded-xl" onClick={() => logoRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1.5" /> {shop.logo_url ? 'Replace logo' : 'Upload logo'}
              </Button>
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
            </div>
            <Button data-testid="settings-save-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl">
              {busy ? 'Saving…' : 'Save settings'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Users */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold font-display">Staff</h2>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              {users ? `${users.active_count} active · limit ${users.user_limit ?? 'unlimited'}` : ''}
            </span>
          </div>
          {users?.users?.map((u) => (
            <div key={u.id} data-testid="settings-user-row" className="flex items-center justify-between gap-2 border-b border-[hsl(var(--border))] last:border-0 pb-2.5 last:pb-0">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{u.name} {!u.active && <span className="text-xs text-[hsl(var(--muted-foreground))]">(inactive)</span>}
                  {u.read_only && <span className="ml-1.5 text-[10px] uppercase rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">read-only</span>}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{u.email} · <span className="capitalize">{u.role}</span></div>
              </div>
              {u.role !== 'owner' && (
                <Button data-testid="settings-user-toggle-button" variant="outline" size="sm" className="rounded-lg shrink-0" onClick={() => toggleUser(u)}>
                  {u.active ? 'Deactivate' : 'Reactivate'}
                </Button>
              )}
            </div>
          ))}
          <Button data-testid="settings-add-user-button" variant="outline" className="w-full h-11 rounded-xl" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4 mr-1.5" /> Add staff member
          </Button>
        </CardContent>
      </Card>

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add staff member</DialogTitle>
            <DialogDescription>Hand these credentials to your staff — they can change the password after logging in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="add-user-name-input" placeholder="Full name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="add-user-email-input" type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="add-user-password-input" placeholder="Temporary password (min 8 chars)" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="h-11 rounded-xl" />
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="add-user-role-advisor"
                onClick={() => setNewUser({ ...newUser, role: 'advisor' })}
                className={`h-11 rounded-xl border text-sm font-medium transition-colors ${newUser.role === 'advisor' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'border-[hsl(var(--border))]'}`}
              >
                Advisor
              </button>
              <button
                type="button"
                data-testid="add-user-role-technician"
                onClick={() => {
                  if (!features.technician) { openUpgrade('Pro'); return; }
                  setNewUser({ ...newUser, role: 'technician' });
                }}
                className={`h-11 rounded-xl border text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${newUser.role === 'technician' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'border-[hsl(var(--border))]'} ${!features.technician ? 'opacity-70' : ''}`}
              >
                {!features.technician && <Lock className="h-3.5 w-3.5" />} Technician
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="add-user-submit-button" disabled={busy || !newUser.name || !newUser.email || newUser.password.length < 8} onClick={addUser} className="w-full h-11 rounded-xl">
              {busy ? 'Adding…' : 'Add user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
